// src/pages/AdminPage.js - Enhanced admin panel with user approval and reset
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getToken } from "../auth";
import {
  Users, UserCheck, UserX, RefreshCw, Trash2, BarChart3,
  AlertTriangle, CheckCircle, XCircle, Clock, Shield, Activity, Phone
} from "lucide-react";

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate("/login");
        return;
      }

      // Fetch pending users
      const pendingResponse = await fetch("https://call-analyzer-backend.onrender.com/pending-users", {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (pendingResponse.status === 403) {
        setError("Access denied. Admin privileges required.");
        return;
      }

      if (pendingResponse.ok) {
        const pendingData = await pendingResponse.json();
        setPendingUsers(pendingData || []);
      }

      // Fetch approved users with analytics
      const usersResponse = await fetch("https://call-analyzer-backend.onrender.com/analytics/users", {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        const usersList = Array.isArray(usersData) ? usersData : (usersData.users || []);
        setUsers(usersList);
        const totalAnalyses = usersList.reduce((sum, u) => sum + (u.total_analyses || 0), 0);
        setStats({ total_analyses: totalAnalyses, total_users: usersList.length });
      }

    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to load data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const approveUser = async (userId, email) => {
    try {
      const token = getToken();
      const response = await fetch(`https://call-analyzer-backend.onrender.com/admin/approve-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId, email: email })
      });

      if (response.ok) {
        setSuccessMessage(`User ${email} approved successfully!`);
        setTimeout(() => setSuccessMessage(""), 3000);
        fetchData();
      } else {
        const data = await response.json();
        setError(data.detail || "Failed to approve user");
      }
    } catch (err) {
      setError("Error approving user: " + err.message);
    }
  };

  const rejectUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to reject and delete ${email}?`)) return;

    try {
      const token = getToken();
      const response = await fetch(`https://call-analyzer-backend.onrender.com/admin/reject-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId, email: email })
      });

      if (response.ok) {
        setSuccessMessage(`User ${email} rejected and removed.`);
        setTimeout(() => setSuccessMessage(""), 3000);
        fetchData();
      } else {
        const data = await response.json();
        setError(data.detail || "Failed to reject user");
      }
    } catch (err) {
      setError("Error rejecting user: " + err.message);
    }
  };

  const resetStats = async () => {
    if (!confirmReset) {
      setConfirmReset(true);
      return;
    }

    setResetting(true);
    try {
      const token = getToken();
      const response = await fetch(`https://call-analyzer-backend.onrender.com/admin/reset-stats`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccessMessage("All stats and analysis data have been reset!");
        setTimeout(() => setSuccessMessage(""), 3000);
        setConfirmReset(false);
        fetchData();
      } else {
        const data = await response.json();
        setError(data.detail || "Failed to reset stats");
      }
    } catch (err) {
      setError("Error resetting stats: " + err.message);
    } finally {
      setResetting(false);
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${email}? This cannot be undone.`)) return;

    try {
      const token = getToken();
      const response = await fetch(`https://call-analyzer-backend.onrender.com/admin/delete-user`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId, email: email })
      });

      if (response.ok) {
        setSuccessMessage(`User ${email} deleted.`);
        setTimeout(() => setSuccessMessage(""), 3000);
        fetchData();
      } else {
        const data = await response.json();
        setError(data.detail || "Failed to delete user");
      }
    } catch (err) {
      setError("Error deleting user: " + err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="h-8 w-8 text-blue-600" />
            Admin Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Manage users, approvals, and system data</p>
        </div>

        {/* CallTools Quick Access */}
        <div className="mb-6">
          <Link
            to="/calltools"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg shadow-lg hover:from-purple-700 hover:to-indigo-700 transition-all"
          >
            <Phone className="h-5 w-5 mr-2" />
            CallTools Dashboard
            <span className="ml-2 text-purple-200">Auto-analyzed calls</span>
          </Link>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <XCircle className="h-5 w-5" />
            {error}
            <button onClick={() => setError("")} className="ml-auto text-red-500 hover:text-red-700">×</button>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            {successMessage}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{users.length + pendingUsers.length}</p>
              </div>
              <Users className="h-10 w-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Approval</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingUsers.length}</p>
              </div>
              <Clock className="h-10 w-10 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Users</p>
                <p className="text-2xl font-bold text-green-600">{users.length}</p>
              </div>
              <UserCheck className="h-10 w-10 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Analyses</p>
                <p className="text-2xl font-bold text-purple-600">{stats?.total_analyses || 0}</p>
              </div>
              <BarChart3 className="h-10 w-10 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Reset Stats Section */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Activity className="h-5 w-5 text-red-500" />
                System Management
              </h2>
              <p className="text-gray-600 text-sm mt-1">Reset all statistics and analysis data to start fresh</p>
            </div>

            {confirmReset ? (
              <div className="flex items-center gap-3">
                <span className="text-red-600 font-medium">Are you sure?</span>
                <button
                  onClick={resetStats}
                  disabled={resetting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {resetting ? "Resetting..." : "Yes, Reset All"}
                </button>
                <button
                  onClick={() => setConfirmReset(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmReset(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
              >
                <RefreshCw className="h-4 w-4" />
                Reset All Stats
              </button>
            )}
          </div>

          {confirmReset && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-800 font-medium">This will permanently delete:</p>
                  <ul className="text-red-700 text-sm mt-1 list-disc list-inside">
                    <li>All call analysis records</li>
                    <li>All transcription history</li>
                    <li>All user statistics</li>
                    <li>All saved reports</li>
                  </ul>
                  <p className="text-red-600 text-sm mt-2 font-medium">This action cannot be undone!</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="border-b">
            <div className="flex">
              <button
                onClick={() => setActiveTab("pending")}
                className={`px-6 py-4 font-medium transition ${
                  activeTab === "pending"
                    ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Clock className="inline h-4 w-4 mr-2" />
                Pending Approval ({pendingUsers.length})
              </button>
              <button
                onClick={() => setActiveTab("approved")}
                className={`px-6 py-4 font-medium transition ${
                  activeTab === "approved"
                    ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <UserCheck className="inline h-4 w-4 mr-2" />
                Approved Users ({users.length})
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === "pending" ? (
              pendingUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <UserCheck className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No pending users awaiting approval</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingUsers.map((user) => (
                    <div key={user.email || user.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div>
                        <p className="font-medium text-gray-900">{user.name || user.email}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Registered: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => approveUser(user.id, user.email)}
                          className="flex items-center gap-1 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                        >
                          <UserCheck className="h-4 w-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => rejectUser(user.id, user.email)}
                          className="flex items-center gap-1 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                        >
                          <UserX className="h-4 w-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Analyses
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.email || user.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.total_analyses || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            user.is_admin
                              ? 'bg-purple-100 text-purple-800'
                              : user.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.is_admin ? 'Admin' : user.status || 'Active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {!user.is_admin && (
                            <button
                              onClick={() => deleteUser(user.id, user.email)}
                              className="text-red-500 hover:text-red-700 transition"
                              title="Delete user"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
